import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from './queries';

export interface Viewer {
  id: string;
  name: string;
}

/**
 * Подписка на живые обновления таблицы. Чужая правка приходит событием и
 * помечает данные устаревшими — react-query перезапрашивает их сам.
 * Своё же изменение прилетит эхом, но лишний запрос дешевле рассинхрона.
 *
 * Если канал недоступен (прокси режет SSE), браузер переподключается сам,
 * а таблица продолжает работать без живых обновлений.
 */
export function useRealtime(docId: string, tableId: string): Viewer[] {
  const qc = useQueryClient();
  const [viewers, setViewers] = useState<Viewer[]>([]);

  useEffect(() => {
    if (!docId || !tableId) return;

    const source = new EventSource(`/api/docs/${docId}/tables/${tableId}/stream`, {
      withCredentials: true,
    });

    const onChanged = () => {
      qc.invalidateQueries({ queryKey: keys.records(docId, tableId) });
      qc.invalidateQueries({ queryKey: keys.columns(docId, tableId) });
      qc.invalidateQueries({ queryKey: ['styles', docId, tableId] });
      qc.invalidateQueries({ queryKey: ['merges', docId, tableId] });
      qc.invalidateQueries({ queryKey: ['table-history', docId, tableId] });
    };

    const onPresence = (e: MessageEvent) => {
      try {
        setViewers(JSON.parse(e.data).viewers ?? []);
      } catch {
        // сломанный кадр — просто игнорируем
      }
    };

    source.addEventListener('changed', onChanged);
    source.addEventListener('presence', onPresence as EventListener);

    return () => {
      source.removeEventListener('changed', onChanged);
      source.removeEventListener('presence', onPresence as EventListener);
      source.close();
      setViewers([]);
    };
  }, [docId, tableId, qc]);

  return viewers;
}
